'use client'
import type { CSSProperties } from 'react'
import { useEffect, useState } from 'react'

const navItems = [
  { href: '#hero', label: 'ホーム' },
  { href: '#about', label: '自己紹介' },
  { href: '#works', label: '制作物' },
  { href: '#stack', label: '技術スタック' },
  { href: '#contact', label: 'お問い合わせ' }
]

const randomWidths = (): [number, number, number] => {
  const pick = () => Math.floor(18 + Math.random() * 12) // 18〜29px の幅
  return [pick(), pick(), pick()]
}

export const Navigation = () => {
  const [open, setOpen] = useState(false)
  const [widths, setWidths] = useState<[number, number, number]>([22, 20, 24])

  useEffect(() => {
    setWidths(randomWidths())
  }, [])

  const handleToggle = () => {
    setOpen((prev) => !prev)
    setWidths(randomWidths())
  }

  return (
    <header className="fixed inset-x-0 top-0 z-30 bg-[rgba(240,240,240,0.9)] backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
        <a href="#hero" className="text-lg font-black tracking-tight text-slate-900">
          Code & Board Lab
        </a>
        <nav className="hidden gap-6 text-sm font-semibold text-slate-700 md:flex" aria-label="Primary">
          {navItems.map((item) => (
            <a key={item.href} className="transition hover:text-[color:var(--accent)]" href={item.href}>
              {item.label}
            </a>
          ))}
        </nav>
        <button
          type="button"
          aria-label="メニューを開閉"
          aria-expanded={open}
          className={`menu-toggle flex flex-col items-end gap-2 md:hidden ${open ? 'menu-toggle-open' : ''}`}
          onClick={handleToggle}
        >
          {widths.map((width, index) => {
            const position: 'top' | 'middle' | 'bottom' = index === 0 ? 'top' : index === 1 ? 'middle' : 'bottom'
            const minWidth = 20
            const maxWidth = 50
            const clamped = Math.min(Math.max(width, minWidth), maxWidth)
            const baseStyle: CSSProperties = {
              width: `${String(clamped)}px`,
              ['--line-width' as never]: `${String(clamped)}px`
            }
            const openStyle: CSSProperties =
              position === 'top'
                ? { transform: 'translateY(7px) rotate(45deg)', width: `${String(clamped)}px` }
                : position === 'middle'
                  ? { opacity: 0, transform: 'scaleX(0)', width: `${String(clamped)}px` }
                  : { transform: 'translateY(-7px) rotate(-45deg)', width: `${String(clamped)}px` }
            return (
              <span
                key={index}
                className={`hamburger-line ${open ? 'hamburger-line-open' : 'hamburger-line-wave'}`}
                data-testid="hamburger-line"
                data-line={position}
                data-open={open}
                style={{
                  ...(open ? openStyle : baseStyle),
                  animationDelay: open ? undefined : `${String(index * 0.12)}s`
                }}
                aria-hidden
              />
            )
          })}
          <span className="pa-text" aria-hidden>
            <span className="pa-text-label">( ᐛ👐)パァ</span>
          </span>
        </button>
      </div>
      <div
        data-testid="nav-links"
        className={`md:hidden ${open ? 'block' : 'hidden'} border-t border-white/60 bg-[rgba(240,240,240,0.95)] px-4 pb-4 pt-2 shadow-[0_6px_24px_rgba(0,0,0,0.08)] backdrop-blur`}
      >
        <nav className="flex flex-col gap-3 text-sm font-semibold text-slate-800" aria-label="Mobile">
          {navItems.map((item) => (
            <a
              key={item.href}
              className="rounded-lg px-2 py-2 transition hover:bg-white hover:text-[color:var(--accent)]"
              href={item.href}
              onClick={() => {
                setOpen(false)
              }}
            >
              {item.label}
            </a>
          ))}
        </nav>
      </div>
    </header>
  )
}
